/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useMemo, useState, useEffect } from "react";

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
	setTeamFilter,
	setGPSStatDefaults,
	setTeamPresets,
} from "../../store/actions/team.actions.js";
import { useDispatch } from "react-redux";
import { setPlayerFilter } from "../../store/actions/player.actions.js";
import { Switch, Row, Col, Button, Select, Space, Table } from "antd";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import "../../Styles/styles-ResultsTable.css";
import "../../Styles/styles-Example.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { getDropdownKey, fillMissingDatesForChart } from "../../Utils/chart_utils";
import { trainingAPI } from "../../api/TrainingAPI";
import {
	MAX_VELO_METRICS,
	TOTAL_DISTANCE_METRICS,
	getMetricsToRender
} from "../../Utils/chart_utils.jsx";
import { useSelector } from "react-redux";
import { StatTileV2 } from "../StatTileV2/StatTileV2.jsx";
import OptionalFeaturesForCharts from "../Charts/OptionalFeaturesForCharts.jsx";
import { mapDataForHighcharts, mapDataForMaxVeloChart } from "../../mappers/model/BarChartMapper.js";
import { stackedBarChartDtoToModel } from "../../mappers/model/StackedBarChartMapper.js";
import {mapDataForHighchartsGroupedBar} from "../../mappers/model/GroupedBarChartMapper.js";
import { mapDataForBarAndBubbleChart } from "../../mappers/model/BarAndBubble.js";
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization.js"
import { CompactStatTile } from "../StatTileV2/CompactStatTile.jsx";
import { useAuth0 } from "@auth0/auth0-react";
import { PresetSelection, PresetType } from "../PresetSelection.tsx";
import { DATE_FORMAT } from "../../Utils/date_utils";
import dayjs from "dayjs";
import {
  getPrediction,
} from "../../Utils/chart_utils";

const todayString = dayjs().format(DATE_FORMAT);

function round(num) {
  return Math.round((num + Number.EPSILON) * 10) / 10;
}

export function PlayerCharts({
	dataIn,
	player_data = false,
	showACWR,
	showTravel,
	showRest,
	type,
	handleComparisonOptionChange,
	dataColorUtil = undefined
}) {
	const dispatch = useDispatch();
	const PRESET_TYPE = PresetType.Gps;
  	const DEFAULT_PRESET = 'Default'

	const {
		loading: {gps: loading},
		games,
		info: { display_name: teamName, metric: teamMetric, restAndTravel, team_colors: teamColors, custom_charts: customChartData,
			acwr_threshold_config: acwrThresholdConfig
		},
		costPerMin,
		gpsStatDefaults,
		dropdowns: {gps: gpsDropdowns},
		all_metric_types,
    	misc: { comparison_types },
		presets
	} = useSelector(({ team }) => team);
	const {
		filter: {
			player,
			showPredictions,
			startDate,
			endDate,
			gpsData,
			showGameDays,
			showWeekly,
			showAllDays,
			comparisonOption
		},
		practicePlan,
		playerCharts
	} = useSelector(({ player }) => player);

	const predictionDates = useMemo(() => {
		return practicePlan.map((e) => e.date)
	}, [practicePlan])

	const {
		user,
	  } = useAuth0();
	
	const [statMetrics, setStatMetrics] = useState([]);
	const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET)
	const [unsavedChanges, setUnsavedChanges] = useState(false)
	
	const enableDrillDown = useMemo(() => {
		return (showGameDays || showWeekly || showACWR || showPredictions || showRest || showTravel) ? false : true
	}, [showGameDays, showWeekly, showACWR, showPredictions, showRest, showTravel])

	let data = dataIn["data"];

	const chartDisplayData = useMemo(
		() => showAllDays && !showWeekly
			? fillMissingDatesForChart(data, startDate, endDate)
			: data,
		[data, showAllDays, showWeekly, startDate, endDate]
	);

	const chartDataIn = { ...dataIn, data: chartDisplayData };

	// const defaultMetricObjects = useMemo(() => {
	// 	return gpsStatDefaults.map((metricIdentifier) =>
	// 		all_metric_types["gps"].find(
	// 			(metric) =>
	// 				getDropdownKey(metric, "metricId") === metricIdentifier
	// 		)
	// 	);
	// }, [gpsStatDefaults, all_metric_types]);

	useMetricDropdowns(['gps']);


	const handleChangeMetric = (index) => (key) => {
		setStatMetrics((prevStatMetrics) => {
			const newStatMetrics = [...prevStatMetrics];
			newStatMetrics[index] = key;
			return newStatMetrics;
		});
		setUnsavedChanges(true)
	};

	const onChangeStats = () => {
		if (type === "team") {
			dispatch(setTeamFilter({ gpsData: !gpsData }));
		} else if (type === "player") {
			dispatch(setPlayerFilter({ gpsData: !gpsData }));
		}
	};

	for (let i = 0; i < dataIn.metrics.length; i++) {
		let metric;
		let wellness;
		if (i < dataIn["metrics"].length) {
			metric = dataIn["metrics"][i];
			wellness = false;
		}

		let key = metric.key,
			maxValue = 0;

		data.map((item, index) => {
			let currVal = data[index][key];
			let per_minute_value = 0;
			if (currVal > maxValue) maxValue = currVal;
			if (item[key + "_per_minute"]) {
				per_minute_value = +item[key + "_per_minute"];
			} else {
				per_minute_value = (+item["total_time"] > 0 ? +(+item[key]/ +item["total_time"]).toFixed(2) : 0)
			}
			const itemWithPerMin = {
				...item,
				[key + "_per_minute"]: per_minute_value,
			};

			dataIn.data[index][key + "_per_minute"] = per_minute_value;

			if (wellness === true || index < 2) {
				return { ...itemWithPerMin };
			} else {
				return {
					...itemWithPerMin,
					[key + "_rolling_average"]:
						(currVal +
							data[index - 1][key] +
							data[index - 2][key]) /
						3,
				};
			}
		});
	}

	const handleSaveMetrics = async () => {
		try {
			if (currentSelectedTilePreset === DEFAULT_PRESET) {
				await trainingAPI.updateDefaultStats(statMetrics, "gps");
				dispatch(setGPSStatDefaults(statMetrics));
			} else {
				const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset)
				await trainingAPI.updatePreset({
					name: currentSelectedTilePreset, type: PRESET_TYPE, config: statMetrics, preset_id: presetModified.preset_id
				});
				presetModified.config = statMetrics
				dispatch(setTeamPresets(presets))
			}
			message.success("Saved!");
		} catch (e) {
			console.log(e);
			message.error("Error. Unable to edit ");
		}
	};

	const getMappingFunction = (metric) => {
		let customChart = null;
		let chartType = null;
		if(metric?.customChartData) {
			customChart = metric?.customChartData
			chartType = metric?.customChartData?.type
		}

		switch (chartType) {
			case 'bar_and_bubble_chart': {
				return mapDataForBarAndBubbleChart({
					dataIn: chartDataIn,
					mainSeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart.bar_metric),
					secondarySeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart.bubble_metric),
					showACWR,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					teamName,
					showPredictions,
					games,
					teamColors: teamColors.all,
					showAvgPlotline: true,
					acwrThresholdConfig,
					customTitle: customChart?.title,
					dataColorUtil,
					enableDrillDown: enableDrillDown,
					selectedPlayerId: player
				});
			}
			case 'grouped_bar_chart': {
				return mapDataForHighchartsGroupedBar({
					dataIn: chartDataIn,
					games,
					metrics: dataIn.metrics.filter( (metric) => metric.key === customChart.bar1_metric || metric.key === customChart.bar2_metric),
					teamColors: teamColors.all,
					isComparison: false,
					showACWR,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					showPredictions,
					isTeamChart: false,
					isMetricComparisonChart: true,
					title: customChart.title,
					dataColorUtil,
					enableDrillDown: enableDrillDown,
					selectedPlayerId: player
				});
			}
			case 'stacked_bar_chart': {
				return stackedBarChartDtoToModel({
				  data: chartDisplayData,
				  showRest,
				  showTravel,
				  restAndTravel,
				  startDate,
				  endDate,
				  chartType: customChart?.stack_type,
				  showPredictions,
				  colors_100: dataIn?.colors,
				  games,
				  teamName,
				  showACWR: customChart?.stack_type === 'distance' ? false : true,
				  customChartConfig: customChart,
				  metrics: dataIn?.metrics,
				  acwrThresholdConfig,
				  enableDrillDown: enableDrillDown,
				  selectedPlayerId: player
				});
			}
			default: {
				// custom chart type either not provided or not recognized.
				// Will get handled by the code below
			}
		}

		if(MAX_VELO_METRICS.concat(TOTAL_DISTANCE_METRICS).includes(metric.key)) {
		  return mapDataForMaxVeloChart({
			dataIn: chartDataIn,
			metric,
			showRest,
			showTravel,
			restAndTravel,
			startDate,
			endDate,
			showACWR,
			games,
			teamName: teamName,
			showPredictions,
			teamColors: teamColors.all,
			acwrThresholdConfig,
			dataColorUtil,
			enableDrillDown: enableDrillDown,
			selectedPlayerId: player
		  })
		}
		 
		return mapDataForHighcharts({
			dataIn: chartDataIn,
			metric,
			showACWR,
			showRest,
			showTravel,
			restAndTravel,
			startDate,
			endDate,
			games,
			teamName: teamName,
			showAvgPlotline: true,
			showPredictions,
			teamColors: teamColors.all,
			teamMetric,
			acwrThresholdConfig,
			dataColorUtil,
			enableDrillDown: enableDrillDown,
			selectedPlayerId: player
		});
	}

	const metricsToRender = getMetricsToRender(teamName, dataIn?.metrics, customChartData?.chart_types);

	const initialColumns = [
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
			sorter: {
				compare: (a, b) => a.date.localeCompare(b.date),
				multiple: 1,
			},
		},
		{
			title: "Game",
			dataIndex: "opponent",
			key: "game",
			sorter: {
				compare: (a, b) => a.opponent?.localeCompare(b?.opponent),
				multiple: 1,
			},
		},
		{
			title: "Total Time",
			dataIndex: "total_time",
			key: "total_time",
			sorter: {
				compare: (a, b) => a.total_time - b.total_time,
				multiple: 2,
			},
			render: (total_time, row) => (
				<span>
				{round(total_time || row.total_time_pre || 0)}
				</span>
			),
		},
	];

	const columns = useMemo(() => {
		return dataIn?.metrics?.filter((e) => e.visible === true || e.visible === undefined).reduce((curr, metric) => {
			let metricName = metric.name
			const key = metric.key;

			return [
				...curr,
				{
					title: metricName,
					dataIndex: key,
					key,
					sorter: {
						compare: (a, b) => a[key] - b[key],
						multiple: curr.length
					},
					render: (value, row) => {
						const curr_date_has_training = row.total_time !== undefined;
						const curr_date_has_plan = (practicePlan.filter(x=>x.date == row.date) || []).length > 0;
						// Day targets: a fixed-values day has no plan; the backend's "_pre" holds the player's target.
						const fixed_prediction = !curr_date_has_plan ? row[`${key}_pre`] : undefined;
						const curr_date_has_prediction = curr_date_has_plan || fixed_prediction != null;
						
						let prediction = 0;
						if (curr_date_has_plan){
							prediction = getPrediction(
							row.date,
							practicePlan,
							costPerMin,
							key
							);
						} else if (fixed_prediction != null) {
							prediction = fixed_prediction;
						}
						
						if ((!value || value === 0) && curr_date_has_prediction && !curr_date_has_training) {
							value = prediction;
						}

						if (!value && !prediction) {
							return null;
						}

						if (value === "" && (key === "RPE" || key === "session_RPE"))
							return null;
						
						const diff = prediction - +value;
						const percentage = (diff / +value) * 100.0;
						const diffColor =  Math.abs(percentage) <= 5 ? "#28a745" : Math.abs(percentage) <= 15 ? "#ffc107" : "#dc3545";
						const diffComp =
							showPredictions &&
							curr_date_has_prediction &&
							curr_date_has_training &&
							prediction !== 0 &&
							value ? (
							<span style={{ color: diffColor }}>
								({percentage === 0 ? "" : value > prediction ? "+" : "-"}
								{Math.abs(round(percentage))}%)
							</span>
							) : null;
			
						let color = "#000000";
						if (dataColorUtil) {
							color = dataColorUtil.get_gps_color(key, value);
						}

						return (
							<>
								<span style={{ color }}>{value}</span> {diffComp}
							</>
						)
					}
				}
			]
		}, initialColumns)
	}, [initialColumns, dataIn])

	return (
		<>
			<div
				className="flex flex-col justify-center items-center w-full"
				id="statistics-widget"
			>
				<Row
				gutter={16}
				style={{ width: "80%", marginBottom: 16, justifyContent: 'center', gap: '10px' }}>
				<PresetSelection 
					presets={presets} 
					user={user} 
					presetType={PRESET_TYPE} 
					statDefaults={gpsStatDefaults} 
					currStatMetrics={statMetrics}
					setStatMetrics={setStatMetrics} 
					currentSelectedTilePreset={currentSelectedTilePreset}
					setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
					comparisonTypes={comparison_types}
					comparisonOption={comparisonOption}
					setComparisonOption={handleComparisonOptionChange}
					handleSave={handleSaveMetrics}
					warnUnsavedChanges={unsavedChanges}
					setWarnUnsavedChages={setUnsavedChanges}
					/>
				</Row>
	  		</div>
			<div
				className="flex flex-col justify-center items-center w-full"
				id="statistics-widget"
			>
				<Row
					gutter={16}
					style={{ width: "90%", marginBottom: 16 }}
					className="exportable tab-testing testing-stats"
				>
					{statMetrics?.map((metric, i) => {
						return (
							<Col span={6} key={i}>

							<CompactStatTile
								dropdowns={gpsDropdowns}
								metric_identifier_with_stat={metric}
								onChange={handleChangeMetric(i)}
								option={comparisonOption}
								endDate={endDate}
								startDate={startDate}
								isCompact={true}
								type={type}
								comparison={comparisonOption}
								playerId={player}			
								/>

								{/* <StatTileV2
									dropdowns={gpsDropdowns}
									value={getDropdownKey(metric, "metricId")}
									index={i}
									onChange={handleChangeMetric(i)}
									metricType={"gps"}
									endDate={endDate}
									stat={gpsData ? "Max" : "Avg"}
									option={option}
									{...(type === "player" && {
										playerId: player,
									})}
									type={type}
								/> */}
							</Col>
						);
					})}
				</Row>
			</div>

			<div className="team-table">
					<Space>
						<div className="resultstable-table exportable tab-gpsdata gpsdv-table">
							<Table
								rowClassName={(row, index) => {
									let classname_string = "";
									if ((row.date > todayString || row.total_time === undefined) && predictionDates.includes(todayString)) {
										classname_string += " team-table-prediction-row";
									}
									return classname_string;
								}}
								columns={columns}
								size="small"
								dataSource={data.filter((dt) => {
									return startDate <= dt.date &&
									dt.date <= endDate &&
                 					(showPredictions || dt.date <= todayString)
								}).sort((a, b) => {
									return dayjs(a.date, 'YYYY-MM-DD') - dayjs(b.date, 'YYYY-MM-DD')
								})}
								pagination={{
									defaultPageSize: 10,
									showSizeChanger: true,
									pageSizeOptions: ["10", "20", "30"],
									position: ["topLeft"],
								}}
								loading={loading}
							/>
						</div>
					</Space>
				</div>
				<div className="outer-example-grid">
					<ul className="list-unstyled example-table-grid" style={{gap: '3rem'}}>
						{
							(dataIn?.metrics?.length && statMetrics?.length && customChartData?.chart_types && metricsToRender?.length) ? (
								metricsToRender
								.map( (metric, i) => 
                                    <div className="example-table-graph" key={`${metric?.key}_${i}`}>
                                        <OptionalFeaturesForCharts
                                            chartType={'bar'}
                                            showAvgPlotline={true}
                                            optionalFeatures={{ showTrendlines: "Trendline" }}
                                            optionsBuilder={(features) => {
                                                const opts = getMappingFunction(metric, features);
                                                return {
                                                    ...opts,
                                                    chart: {
                                                        ...(opts?.chart || {}),
                                                        height: '100%'
                                                    }
                                                };
                                            }}
                                        />
                                    </div>)
						): <></>}
					</ul>
				</div>
		</>
	);
}
